/**
 * The two popup buttons, and the one line the owner drew beside them.
 *
 * POPUP FIRST IS RULED, not a preference: "Browser sign-in is the cold-start path only: the
 * ordinary Firebase JS SDK, popup first" (adr/0007-web-app-stack.md). There is no
 * "use a redirect instead" button here, and there must not be one until every item in
 * ../firebase/redirect-fallback.ts is live — before then redirect fails wherever third-party
 * storage is blocked, which is every current Chrome, Safari and Firefox.
 *
 * WHY BOTH BUTTONS GO BUSY, NOT JUST THE ONE PRESSED. Only one popup can be open at a time, and a
 * second click while the first window is opening is one of the ways browsers decide the page is
 * being abusive. Disabling the pair is cheaper than explaining the result.
 *
 * THE LABELS ARE OURS. Layout and wording are ours to decide (decision-log row 99). "Continue
 * with" rather than "Sign in with" because the same buttons do double duty on the link prompt,
 * where nobody is signing in.
 */
import { Button, Stack, Text } from '@mantine/core';
import { useTranslation } from '../../../i18n';
import { NO_PASSWORD_LINE } from '../copy';
import type { PopupProvider } from '../firebase/auth-port';

export interface ProviderButtonsProps {
  readonly onChoose: (provider: PopupProvider) => void;
  /** Which one is in flight, or null. Both buttons disable; only this one shows the spinner. */
  readonly busyProvider: PopupProvider | null;
  readonly disabled?: boolean;
  /** Shown under the buttons. Defaults to the owner's own line. */
  readonly footnote?: string;
  readonly appleLabel?: string;
  readonly googleLabel?: string;
}

export function ProviderButtons({
  onChoose,
  busyProvider,
  disabled = false,
  footnote,
  appleLabel,
  googleLabel,
}: ProviderButtonsProps) {
  const { t } = useTranslation('auth');
  const anyBusy = busyProvider !== null;

  return (
    <Stack gap={12}>
      <Button
        fullWidth
        vars={() => APPLE_LOOK}
        onClick={() => {
          onChoose('apple');
        }}
        loading={busyProvider === 'apple'}
        disabled={disabled || (anyBusy && busyProvider !== 'apple')}
      >
        {appleLabel ?? t('providers.continueWithApple')}
      </Button>
      <Button
        fullWidth
        vars={() => GOOGLE_LOOK}
        onClick={() => {
          onChoose('google');
        }}
        loading={busyProvider === 'google'}
        disabled={disabled || (anyBusy && busyProvider !== 'google')}
      >
        {googleLabel ?? t('providers.continueWithGoogle')}
      </Button>
      <Text size="xs" c="dimmed" ta="center">
        {footnote ?? t(NO_PASSWORD_LINE)}
      </Text>
    </Stack>
  );
}

/*
 * ★ THE PROTOTYPE'S TWO BUTTONS (the owner's live look at real cabinets, 2026-10-01 / design 15):
 * full width, 54px tall with 12px corners, Apple filled in the text colour with the page's colour
 * for its words - dark on light, light on dark - and Google outlined. Set through Mantine's own
 * variables (`vars`), so the hover, the disabled state and the loader all follow them. The
 * prototype's two brand marks are not drawn: neither is in the icon set (web/ui/src/icons.ts), and
 * Google's is four raw colours, which the no-raw-colour rule refuses.
 */
const SHAPE = {
  '--button-height': '54px',
  '--button-radius': '12px',
  '--button-fz': '14px',
  '--button-padding-x': '18px',
} as const;

const APPLE_LOOK = {
  root: {
    ...SHAPE,
    '--button-bg': 'oklch(var(--mx-text))',
    '--button-hover': 'oklch(var(--mx-text) / 0.88)',
    '--button-color': 'oklch(var(--mx-bg))',
    '--button-hover-color': 'oklch(var(--mx-bg))',
    '--button-bd': '1px solid oklch(var(--mx-text))',
  },
};

const GOOGLE_LOOK = {
  root: {
    ...SHAPE,
    '--button-bg': 'transparent',
    '--button-hover': 'oklch(var(--mx-surface2))',
    '--button-color': 'oklch(var(--mx-text))',
    '--button-hover-color': 'oklch(var(--mx-text))',
    '--button-bd': '1px solid oklch(var(--mx-border2))',
  },
};
